import { IconCheck } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { createMutation } from '../lib/mutation';
import { fetchOssProjectStats, isRepoFullName, type OssProjectStats } from '../lib/publicOss';
import { Field } from './Field';
import { Button, buttonClass } from './ui/Button';

// Step 1 of the donate wizard (arc 101 slice 11, plan doc 05): which project.
//
// The whole point of this step is that it runs BEFORE any charge. A bad slug —
// a typo, a private repo, a project whose maintainer never opted in — must fail
// here, because the next step hands the browser to Stripe and a subscription is
// not undone by a back button. `GET /v1/public/oss/:owner/:repo/stats` answers
// that question with no bearer, so the check costs nothing and works even
// before the donor has any relationship with the project: its 404 and a 200
// without the donation block (a repo the owner has consented to publish its
// agent-authored numbers on but has not opted in to donations,
// `isDonationStats` in `lib/publicOss.ts`) both read as not fundable.
//
// The 404 folds the ineligible cases into one answer on purpose (a pre-auth
// route must not leak which), so the refusal copy lists the possibilities rather
// than claiming one.
export type DonateRepoStepProps = {
  /** The project already confirmed, if the donor is resuming. */
  readonly project: string | null;
  readonly onConfirmed: (fullName: string) => void;
  readonly onReset: () => void;
};

export function DonateRepoStep(props: DonateRepoStepProps): JSX.Element {
  const [slug, setSlug] = createSignal('');
  const [stats, setStats] = createSignal<OssProjectStats | null>(null);
  // Two things the lookup itself never rejects over: a slug that is not an
  // `owner/repo` at all (nothing to ask about), and a 200 that answers "no such
  // donatable project". Neither is a failed request, so neither can live in the
  // mutation's `error()`.
  const [verdict, setVerdict] = createSignal<string | null>(null);

  const lookup = createMutation({
    call: (value: string) => {
      setVerdict(null);
      return fetchOssProjectStats(value);
    },
    // Not a 404: a rate limit or an outage is "ask again", never "no such
    // project" — answering it as ineligible would refuse a real one.
    fallback: t('donations.repo_error'),
    onSuccess: (found) => {
      if (found === null) {
        setVerdict(t('donations.repo_not_donatable', { project: slug().trim() }));
        return;
      }
      setStats(found);
      // The SERVER's spelling of the name, not the donor's — casing differences
      // would otherwise make the gift's own row look like a different project.
      props.onConfirmed(found.full_name);
    },
  });
  const busy = lookup.pending;
  const error = (): string | null => lookup.error() ?? verdict();

  const check = (e: Event): void => {
    e.preventDefault();
    const value = slug().trim();
    if (!isRepoFullName(value)) {
      setVerdict(t('donations.repo_invalid'));
      return;
    }
    void lookup.run(value);
  };

  return (
    <Show
      when={props.project === null}
      fallback={
        <div class="field-group">
          <div class="alert alert--icon" role="status">
            <IconCheck />
            <p class="alert__title">
              {t('donations.repo_confirmed', { project: props.project ?? '' })}
            </p>
            <Show when={stats()}>
              {(s) => (
                <p class="alert__desc">
                  {tPlural('donations.repo_backers', s().backers, {
                    count: String(s().backers),
                  })}
                </p>
              )}
            </Show>
          </div>
          <div class="dialog__footer">
            <Button
              variant="outline"
              onClick={() => {
                setStats(null);
                setSlug('');
                props.onReset();
              }}
            >
              {t('donations.repo_change')}
            </Button>
          </div>
        </div>
      }
    >
      <form class="field-group" onSubmit={check}>
        <Field
          label={t('donations.repo_label')}
          id="donate-repo"
          desc={t('donations.repo_desc')}
          error={error()}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              value={slug()}
              placeholder={t('donations.repo_placeholder')}
              onInput={(e) => {
                setSlug(e.currentTarget.value);
                setVerdict(null);
                lookup.clearError();
              }}
              required
            />
          )}
        </Field>
        <div class="dialog__footer">
          <button type="submit" class={buttonClass()} disabled={busy() || slug().trim() === ''}>
            {t(busy() ? 'donations.repo_checking' : 'donations.repo_check')}
          </button>
        </div>
      </form>
    </Show>
  );
}
